// WP-C1.6 (RM-2/3/4, S-36 `/reminders`): list (text, when, recurrence, linked entity chip,
// channel; snoozed shown muted) + "+ New" (text + a native datetime-local "when" + a simple
// recurrence picker). RM-2's own worked example ("remind me tomorrow 5pm to...") already ships
// as a chat NL intent (WP-C1.6 backend) — this form is a structured fallback for the app, not
// a re-implementation of that parser in the browser.
import type { ReminderCreateRequest, ReminderResponse } from '@pfos/shared';
import { Bell, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { EmptyState } from '../components/EmptyState';
import { Field, ghostButtonInlineClass, inputClass, selectClass } from '../components/FormKit';
import { FormSheet } from '../components/FormSheet';
import {
  useCancelReminder,
  useCreateReminder,
  useReminders,
  useSnoozeReminder,
} from '../lib/reminders';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function formatWhen(iso: string): string {
  const label = new Date(iso).toLocaleString(undefined, {
    timeZone: 'Asia/Karachi',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  return `${label} PKT`;
}

function describeRecurrence(schedule: ReminderResponse['schedule']): string | null {
  if (schedule === undefined) return null;
  const label = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly', yearly: 'Yearly' }[
    schedule.frequency
  ];
  if (schedule.frequency === 'weekly' && schedule.weekday !== undefined) {
    return `${label} · ${WEEKDAYS[schedule.weekday] ?? ''}`;
  }
  return label;
}

function linkHref(linkType: string | undefined): string | null {
  if (linkType === 'rp') return '/owed';
  if (linkType === 'recurringTemplate') return '/recurring';
  return null;
}

const SNOOZE_PRESETS: { label: string; minutes: number }[] = [
  { label: '15 min', minutes: 15 },
  { label: '1 hour', minutes: 60 },
  { label: 'Tomorrow', minutes: 24 * 60 },
];

function ReminderRow({ reminder }: { reminder: ReminderResponse }): React.JSX.Element {
  const cancelMutation = useCancelReminder();
  const snoozeMutation = useSnoozeReminder();
  const recurrence = describeRecurrence(reminder.schedule);
  const href = linkHref(reminder.linkType);

  return (
    <li
      className={`flex flex-wrap items-center justify-between gap-3 border-b border-border py-3 last:border-0 ${
        reminder.snoozed ? 'opacity-60' : ''
      }`}
    >
      <div>
        <p className="text-sm text-ink">{reminder.text}</p>
        <p className="text-xs text-muted">
          {formatWhen(reminder.nextRunAt)}
          {recurrence !== null && ` · ${recurrence}`} · WhatsApp
          {reminder.snoozed && ' · Snoozed'}
        </p>
        {href !== null && (
          <a href={href} className="text-xs text-primary hover:underline">
            Linked record
          </a>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1">
        {SNOOZE_PRESETS.map((preset) => (
          <button
            key={preset.label}
            type="button"
            className={ghostButtonInlineClass}
            disabled={snoozeMutation.isPending}
            onClick={() => {
              snoozeMutation.mutate({ id: reminder.id, minutes: preset.minutes });
            }}
          >
            {preset.label}
          </button>
        ))}
        <button
          type="button"
          className={ghostButtonInlineClass}
          disabled={cancelMutation.isPending}
          onClick={() => {
            cancelMutation.mutate(reminder.id);
          }}
        >
          Cancel
        </button>
      </div>
    </li>
  );
}

type Frequency = '' | 'daily' | 'weekly' | 'monthly';

function NewReminderForm({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.JSX.Element {
  const [text, setText] = useState('');
  const [when, setWhen] = useState('');
  const [frequency, setFrequency] = useState<Frequency>('');
  const [error, setError] = useState<string | null>(null);
  const createMutation = useCreateReminder();

  useEffect(() => {
    if (open) {
      setText('');
      setWhen('');
      setFrequency('');
      setError(null);
    }
  }, [open]);

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title="New reminder"
      submitLabel="Save"
      submitting={createMutation.isPending}
      onSubmit={() => {
        setError(null);
        if (text.trim() === '') {
          setError('Reminder text is required');
          return;
        }
        if (when === '') {
          setError('Pick a date and time');
          return;
        }
        const nextRunAt = new Date(when).toISOString();
        const runAtDate = new Date(nextRunAt);
        let schedule: ReminderCreateRequest['schedule'];
        if (frequency === 'daily') schedule = { frequency: 'daily', everyN: 1 };
        else if (frequency === 'weekly')
          schedule = { frequency: 'weekly', everyN: 1, weekday: runAtDate.getUTCDay() };
        else if (frequency === 'monthly') {
          schedule = {
            frequency: 'monthly',
            everyN: 1,
            monthlyRule: { kind: 'day', day: runAtDate.getUTCDate() },
          };
        }
        createMutation.mutate(
          { text: text.trim(), nextRunAt, ...(schedule !== undefined ? { schedule } : {}) },
          {
            onSuccess: () => {
              onOpenChange(false);
            },
          },
        );
      }}
    >
      {error !== null && <p className="mb-3 text-sm text-danger">{error}</p>}
      <Field label="Reminder" htmlFor="reminder-text">
        <input
          id="reminder-text"
          className={inputClass}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
          }}
        />
      </Field>
      <Field label="When" htmlFor="reminder-when">
        <input
          id="reminder-when"
          type="datetime-local"
          className={inputClass}
          value={when}
          onChange={(e) => {
            setWhen(e.target.value);
          }}
        />
      </Field>
      <Field label="Repeat" htmlFor="reminder-frequency">
        <select
          id="reminder-frequency"
          className={selectClass}
          value={frequency}
          onChange={(e) => {
            setFrequency(e.target.value as Frequency);
          }}
        >
          <option value="">Doesn&apos;t repeat</option>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
        </select>
      </Field>
    </FormSheet>
  );
}

export function RemindersPage(): React.JSX.Element {
  const { data: reminders, isLoading } = useReminders('active');
  const [formOpen, setFormOpen] = useState(false);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold text-ink">Reminders</h1>
        <button
          type="button"
          className="flex items-center gap-1 rounded-[var(--radius-control)] bg-primary px-3 py-1.5 text-sm font-medium text-primary-ink hover:opacity-90 active:opacity-80"
          onClick={() => {
            setFormOpen(true);
          }}
        >
          <Plus size={16} /> New reminder
        </button>
      </div>

      {isLoading && <p className="text-sm text-muted">Loading…</p>}

      {!isLoading && (reminders === undefined || reminders.length === 0) && (
        <EmptyState
          icon={Bell}
          message="No reminders set."
          action={{
            label: 'New reminder',
            onClick: () => {
              setFormOpen(true);
            },
          }}
        />
      )}

      {reminders !== undefined && reminders.length > 0 && (
        <ul className="rounded-[var(--radius-card)] border border-border bg-surface px-4">
          {reminders.map((r) => (
            <ReminderRow key={r.id} reminder={r} />
          ))}
        </ul>
      )}

      <NewReminderForm open={formOpen} onOpenChange={setFormOpen} />
    </div>
  );
}
